import "./Lock.scss";
import {
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  IonImg,
  IonFooter,
  IonButton,
} from "@ionic/react";
import React, { useEffect, useRef } from "react";
import { AuthMode, VaultErrorCodes } from "@ionic-enterprise/identity-vault";

import { useTracked } from "../../state";
import { identityService } from "../../services/vault";
import { getUserByIdentity } from "../../api/user";
import lockedIcon from "../../assets/svg/locked.svg";
import phoneLockImg from "../../assets/svg/phone-lock.svg";
import { ShowToastErrorMessage } from "../../utils/helpers/ToastMessage";
import { obscureEmail } from "../../utils/helpers/String";

interface Props {
  user: any;
  authMode: any;
  deviceId: any;
  lastUser: any;
  pushToken: any;
}

const Lock: React.FC<Props> = React.memo(
  ({ user, authMode, deviceId, pushToken, lastUser = {} }) => {
    const [state, dispatch] = useTracked();
    // const [isLoading, setIsLoading] = useState(false);
    const isUnmounted = useRef<boolean>(false);

    useEffect(() => {
      return () => {
        isUnmounted.current = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pushToken]);

    // local and tracked state can't grab the latest value, so here it is.
    let localPINFailedAttempts = 0;

    const handleUnlock = async () => {

      try {
        // keep using passcode unlock when there was a pasccode failed attempts before
        if (
          authMode === AuthMode.BiometricAndPasscode &&
          localPINFailedAttempts > 0
        ) {
          await identityService.unlock(AuthMode.PasscodeOnly);
        } else {
          // default unlock, vault decides what mode was used to lock.
          await identityService.unlock();
        }

        const session = await identityService.restoreSession();

        dispatch({ type: "setSession", session });

        if (session && session.token && session.identity) {
          // setIsLoading(true);
          let user: any;

          if (lastUser?.identity) {
            user = await getUserByIdentity(lastUser?.identity.toLowerCase());
          } else {
            user = await getUserByIdentity(session.identity.toLowerCase());
          }

          if (user?.data?.id) {
            const { aliases, issuers, identity } = user.data;
            dispatch({ user: user.data, type: "setUser" });

            if (issuers) {
              dispatch({ issuers, type: "setIssuers" });
            }
            if (aliases) {
              dispatch({ aliases, type: "setAliases" });
            }
            dispatch({ identity, type: "setIdentity" });
            dispatch({ token: session.token, type: "setToken" });

            localStorage.removeItem("isAppLocked");
            // history.replace("/page/Verify");
          } else {
            ShowToastErrorMessage(
              "Cannot retrieve account information. Please try again."
            );
          }

          if (!isUnmounted.current) {
            // setIsLoading(false);
          }
        }

        if (
          authMode === AuthMode.PasscodeOnly ||
          authMode === AuthMode.BiometricAndPasscode
        ) {
          dispatch({ type: "hideApplicationsPIN" });
          dispatch({ type: "setPINFailedAttempts", value: 0 });
        }
      } catch (e) {
        if (!isUnmounted.current) {
          // setIsLoading(false);
        }

        if (
          (authMode === AuthMode.PasscodeOnly ||
            authMode === AuthMode.BiometricAndPasscode) &&
          (e as any).code === VaultErrorCodes.AuthFailed
        ) {
          dispatch({
            type: "setPINFailedAttempts",
          });

          localPINFailedAttempts = localPINFailedAttempts + 1;

          // try to pin unlock again until 3 attempts
          handleUnlock();
        }

        if ((e as any).code === VaultErrorCodes.TooManyFailedAttempts) {
          ShowToastErrorMessage("Too many failed attempts. Try again later.");
        }

        // @ts-ignore
        if (e.message && state.mqm) {
          await state.mqm.pubnub.publish({
            channel: deviceId,
            meta: {
              type: "setNotification",
            },
            message: {
              isError: true,
              // @ts-ignore
              message: e.message,
            },
          });
        }
      }
    };

    return (
      <IonPage className="lock-page">
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonTitle>
              <IonIcon className="lock-icon" icon={lockedIcon} />
            </IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div className="container">
            <IonImg className="image" src={phoneLockImg} />
            <div className="title">Verifier Locked</div>
            <div className="subtitle">
              For account {obscureEmail(user.email)}
            </div>
          </div>
        </IonContent>
        <IonFooter>
          <IonButton
            color="danger"
            expand="block"
            shape="round"
            onClick={handleUnlock}
          >
            Unlock
          </IonButton>
        </IonFooter>
      </IonPage>
    );
  }
);

const MemoizedLock = () => {
  const [state] = useTracked();
  const { user, authMode, deviceId, pushToken, lastUser } = state;
  return (
    <Lock
      user={user}
      authMode={authMode}
      deviceId={deviceId}
      lastUser={lastUser}
      pushToken={pushToken}
    />
  );
};

export default MemoizedLock;
